import {Component, EventEmitter, OnDestroy, OnInit, Output} from '@angular/core';
import {Subscription} from 'rxjs';
import {NgbCollapse} from '@ng-bootstrap/ng-bootstrap';

import {LeftMenuFavoriteService} from '@services/left-menu/favorites/left-menu-favorite.service';
import {ToggleFavoriteService} from '@services/left-menu/favorites/toggle-favorite/toggle-favorite.service';
import {LeftMenuService} from '@services/left-menu/left-menu.service';
import {DesignService} from '@services/left-menu/design/design.service';

import {MenuItem} from '@models/sidebar/menu-item';
import {MenuFavoriteItem} from '@models/sidebar/response/menu-favorite-item';
import {MenuItemType} from '@models/sidebar/menu-item-type';
import {MenuNotification, OperationType} from '@models/sidebar/menu-notification';
import {
  FavoriteNotificationService
} from '@services/left-menu/favorites/favorite-notification/favorite-notification.service';
import {TableHeaderSortableService} from '@services/table-header-sortable/table-header-sortable.service';
import {SelectPageService} from '@services/left-menu/select-page/select-page.service';
import {PageItem} from '@models/sidebar/page-item';
import {UpdateUrlService} from '@services/update-url/update-url.service';

@Component({
    selector: 'scitis-favorites',
    templateUrl: './favorites.component.html',
    styleUrls: ['./favorites.component.scss'],
    standalone: false
})
export class FavoritesComponent implements OnInit, OnDestroy {

  @Output()
  menuItemSelected: EventEmitter<MenuItem> = new EventEmitter<MenuItem>();

  @Output()
  expand: EventEmitter<boolean> = new EventEmitter<boolean>();

  menuFavoriteItems: MenuFavoriteItem[] = [];
  isFavoritesCollapsed = true;
  subscription: Subscription = new Subscription();
  itemSubscription: Subscription = new Subscription();
  updateUrlSubscription: Subscription = new Subscription();

  constructor(
      private leftMenuFavoriteService: LeftMenuFavoriteService,
      private toggleFavoriteService: ToggleFavoriteService,
      private leftMenuService: LeftMenuService,
      private designService: DesignService,
      private favoriteNotificationService: FavoriteNotificationService,
      private tableHeaderSortableService: TableHeaderSortableService,
      private selectPageService: SelectPageService,
      private updateUrlService: UpdateUrlService) {
    this.setMenuItems();
  }

  get displayFavoritesItems(): boolean {
    return this.menuFavoriteItems && this.menuFavoriteItems.length > 0;
  }

  get numberOfFavoritesItems(): number {
    return this.menuFavoriteItems.length;
  }

  ngOnInit() {
    this.subscription = this.toggleFavoriteService.updatedFavoriteMenuItems
    .subscribe((updatedFavoriteMenuItems: MenuFavoriteItem[]) => {
      this.menuFavoriteItems = updatedFavoriteMenuItems.filter((item) => item.type != MenuItemType.MACHINE_GROUP_STATUS_PAGE);
    });
    this.itemSubscription = this.favoriteNotificationService.currentMenuNotification
    .subscribe((message: MenuNotification) => {
      this.refreshMenu(message);
    });

    this.updateUrlSubscription = this.updateUrlService.updateUrlMenuNotification
    .subscribe((pageItem: PageItem) => {
      this.menuFavoriteItems.forEach(item => {
        if (String(item.id) === String(pageItem.id)) {
          item.url = pageItem.url;
        }
      });
    });
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
    this.itemSubscription.unsubscribe();
    this.updateUrlSubscription.unsubscribe();
  }

  onMenuItemSelected(menuItem: MenuItem): void {
    this.setSelectedPageId(menuItem.id);
    if (menuItem.parentId) {
      this.menuItemSelected.emit(menuItem);
    } else {
      if (menuItem.menuItemParent && menuItem.menuItemParent.id) {
        const parentIsMachine = menuItem.menuItemParent.isMachine || menuItem.menuItemParent.type === MenuItemType.NODE;
        this.leftMenuService.getParent(menuItem.menuItemParent.id, parentIsMachine).subscribe({
          next: menuItemParent => {
            menuItem.menuItemParent = Object.assign(new MenuItem(), menuItemParent);
            if (menuItem.menuItemParent) {
              menuItem.menuItemParent.icon = this.designService.getIcon(menuItem.menuItemParent.type);
            }
            menuItem.parentId = menuItemParent.id;
            this.menuItemSelected.emit(menuItem);
          }, error: error => {
            console.log(error);
          }
        });
      }
    }
  }

  toggleFavorites(favoritesCollapse: NgbCollapse): void {
    favoritesCollapse.toggle();
    if (!favoritesCollapse.collapsed) {
      this.expand.emit(true);
    }
  }

  getClass(menuFavoriteItem: MenuFavoriteItem) {
    if (String(menuFavoriteItem.id) === String(this.selectPageService.getSelectedPageId)) {
      return 'scitis-menu-item-active';
    }
    return '';
  }

  private setSelectedPageId(value: string | null): void {
    this.selectPageService.setSelectedPageId(value);
  }

  private setMenuItems(): void {
    this.leftMenuFavoriteService.listFavorites().subscribe({
      next: (result: MenuFavoriteItem[]) => {
        this.menuFavoriteItems = result.filter((item) => item.type != MenuItemType.MACHINE_GROUP_STATUS_PAGE);
        this.menuFavoriteItems = this.tableHeaderSortableService.sortItems<MenuFavoriteItem>(this.menuFavoriteItems, 'timestamp', 'asc');
        this.toggleFavoriteService.setFavoriteMenuItems(this.menuFavoriteItems);
      }, error: error => {
        console.log(error);
      }
    });
  }

  private refreshMenu(message: MenuNotification): void {
    if (FavoritesComponent.shouldUpdateName(message)) {
      this.updateName(message);
    } else if (FavoritesComponent.shouldUpdateParent(message)) {
      this.updateParent(message);
    } else if (FavoritesComponent.itemIsDeleted(message)) {
      this.deleteItem(message);
    } else if (FavoritesComponent.itemIsSharedOrUnShared(message)) {
      this.setMenuItems();
    }
  }

  private updateParent(message: MenuNotification) {
    for (const item of this.menuFavoriteItems) {
      if (FavoritesComponent.updateMenuItem(item, message)) {
        item.menuItemName = message.newName;
      } else if (FavoritesComponent.updateItemParent(item, message)) {
        item.itemParentName = message.newName;
        item.itemParentId = message.name;
      }
    }
  }

  private updateName(message: MenuNotification) {
    for (const item of this.menuFavoriteItems) {
      if (FavoritesComponent.updateMenuItem(item, message)) {
        item.menuItemName = message.newName;
      } else if (FavoritesComponent.updateItemParent(item, message)) {
        item.itemParentName = message.newName;
      }
    }
  }

  private deleteItem(message: MenuNotification) {
    const itemsToDelete: MenuFavoriteItem[] = [];
    for (const item of this.menuFavoriteItems) {
      if (FavoritesComponent.updateMenuItem(item, message) || FavoritesComponent.updateItemParent(item, message) || FavoritesComponent.updateNodeItemParent(item, message)) {
        itemsToDelete.push(item);
      }
    }
    if (itemsToDelete.length > 0) {
      for (const item of itemsToDelete) {
        const index = this.menuFavoriteItems.indexOf(item, 0);
        this.menuFavoriteItems.splice(index, 1);
      }
    }
  }

  private static shouldUpdateName(message: MenuNotification): boolean {
    return message.operation === OperationType.UPDATE_NAME;
  }

  private static shouldUpdateParent(message: MenuNotification): boolean {
    return message.operation === OperationType.UPDATE_USER_MACHINERY_GROUP;
  }

  private static itemIsDeleted(message: MenuNotification): boolean {
    return message.operation === OperationType.DELETE_PAGE || message.operation === OperationType.DELETE;
  }

  private static itemIsSharedOrUnShared(message: MenuNotification): boolean {
    return message.operation === OperationType.SHARE_OR_UN_SHARE_PAGE;
  }

  private static updateMenuItem(item: MenuFavoriteItem, message: MenuNotification): boolean {
    const separatedMenuItemId: string[] = item.id.split('_');
    return String(item.id) === String(message.id) || (message.isMachine && item.id.endsWith(String(message.id)))
        || separatedMenuItemId[1] === String(message.id);
  }

  private static updateItemParent(item: MenuFavoriteItem, message: MenuNotification): boolean {
    return String(item.itemParentId) === String(message.id) || (message.isMachine && item.itemParentId.endsWith(String(message.id))) || (message.isTemplatePage && item.menuItemId.endsWith(String(message.id)));
  }

  private static updateNodeItemParent(item: MenuFavoriteItem, message: MenuNotification): boolean {
    return item.isUserMenuItem && !item.itemParentMachine && item.nodeItemParentId === String(message.id);
  }

}
